import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  BarChart2,
  Bot,
  Clock,
  MessageSquare,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import DashboardLayout from "../dashboard/layout/DashboardLayout";
import PageHeader from "../dashboard/layout/PageHeader";
import { SentryTestButton } from "../debug/SentryTestButton";

type Stats = {
  totalAssistants: number;
  activeAssistants: number;
  totalConversations: number;
  averageResponseTime: string;
  totalUsers: number;
  successRate: string;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({
    totalAssistants: 0,
    activeAssistants: 0,
    totalConversations: 0,
    averageResponseTime: "0s",
    totalUsers: 0,
    successRate: "0%",
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    const userdata = await supabase.auth.getUser();
    const userdata1 = await supabase.auth.getClaims();

    setIsLoading(true);
    try {
      // No need to fetch organization_id as RLS policies will handle this
      const { data: assistantsData, error: assistantsError } = await supabase
        .from("assistants")
        .select("id, status");

      if (!assistantsError && assistantsData) {
        const activeAssistants = assistantsData.filter(
          (a) => a.status === "published",
        ).length;
        setStats((prev) => ({
          ...prev,
          totalAssistants: assistantsData.length,
          activeAssistants,
          totalConversations: 1250,
          averageResponseTime: "1.5s",
          totalUsers: 85,
          successRate: "94.5%",
        }));
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Assistants",
      value: stats.totalAssistants,
      description: "Total number of assistants created",
      icon: <Bot className="h-6 w-6 text-primary" />,
    },
    {
      title: "Active Assistants",
      value: stats.activeAssistants,
      description: "Currently active assistants",
      icon: <Activity className="h-6 w-6 text-primary" />,
    },
    {
      title: "Total Conversations",
      value: stats.totalConversations,
      description: "Conversations handled by assistants",
      icon: <MessageSquare className="h-6 w-6 text-primary" />,
    },
    {
      title: "Average Response Time",
      value: stats.averageResponseTime,
      description: "Average time to respond",
      icon: <Clock className="h-6 w-6 text-primary" />,
    },
    {
      title: "Total Users",
      value: stats.totalUsers,
      description: "Users interacting with assistants",
      icon: <Users className="h-6 w-6 text-primary" />,
    },
    {
      title: "Success Rate",
      value: stats.successRate,
      description: "Successful conversation rate",
      icon: <BarChart2 className="h-6 w-6 text-primary" />,
    },
  ];

  return (
    <DashboardLayout activeItem="Dashboard">
      <div className="space-y-6">
        <PageHeader
          title="Dashboard Overview"
          description="Monitor your AI assistants performance and usage statistics"
        />

        <SentryTestButton />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            // Determine color based on index
            const colors = [
              { bg: "bg-secondary/20", border: "border-border-border", text: "text-foreground dark:text-ink-faint", icon: "text-foreground" },
              { bg: "bg-good-bg-good-bg/20", border: "border-good-line", text: "text-foreground dark:text-ink-faint", icon: "text-good" },
              { bg: "bg-secondary/20", border: "border-border-border", text: "text-foreground dark:text-ink-faint", icon: "text-foreground" },
              { bg: "bg-mid-bg-mid-bg/20", border: "border-mid-line", text: "text-foreground dark:text-ink-faint", icon: "text-mid" },
              { bg: "bg-danger-bg-danger-bg/20", border: "border-danger-line", text: "text-foreground dark:text-ink-faint", icon: "text-danger" },
              { bg: "bg-secondary/20", border: "border-border-border", text: "text-foreground dark:text-ink-faint", icon: "text-foreground" },
            ];
            const colorSet = colors[index % colors.length];

            return (
              <div
                key={index}
                className={`relative overflow-hidden ${colorSet.bg} p-6 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-105 hover:-translate-y-1 border ${colorSet.border} group`}
              >
                <div className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <h3 className="text-lg font-medium text-foreground">{stat.title}</h3>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center bg-card/60 dark:bg-black/20 backdrop-blur-sm transform transition-all duration-300 group-hover:scale-110 ${colorSet.icon}`}>
                    {stat.icon}
                  </div>
                </div>
                <div className={`text-2xl font-bold mt-1 ${colorSet.text} transition-all duration-300 group-hover:translate-x-1 group-hover:scale-105`}>
                  {stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1 transition-all duration-300 group-hover:opacity-90">
                  {stat.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center mt-8">
          <Button
            onClick={() => navigate("/assistants")}
            className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium"
          >
            <Bot className="mr-2 h-4 w-4" />
            View All Assistants
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;